import { createContext, useContext, useState } from "react";
import { useAuth } from "./auth";

const translations = {
  de: {
    brand: "Weissblom",
    subtitle: "Agentur für Immobilienbetreuung",
    login: {
      title: "Willkommen",
      lead: "Ihre private Immobilienbetreuung. Anmeldung nur für freigeschaltete Mitglieder.",
      emailLabel: "E-Mail",
      emailPlaceholder: "ihre.adresse@beispiel.de",
      requestCode: "Zugangscode anfordern",
      codeSent: "Ein 8-stelliger Code wurde an Ihre E-Mail gesendet.",
      codeLabel: "8-stelliger Zugangscode",
      verify: "Anmelden",
      back: "Andere E-Mail",
      notAllowed: "Diese E-Mail ist nicht freigeschaltet.",
      needAccess: "Ich habe noch keinen Zugang",
      inviteHint: "Neue Mitglieder werden durch unser Team hinzugefügt. Kontakt: info@weissblom.de",
    },
    nav: {
      dashboard: "Übersicht",
      booking: "Abwesenheit planen",
      chat: "Nachrichten",
      requests: "Anfragen",
      catalogue: "Leistungen",
      updates: "Wochenberichte",
      handbook: "Handbuch",
      family: "Familie",
      settings: "Einstellungen",
      team_dashboard: "Team-Übersicht",
      calendar: "Kalender",
      customers: "Kunden",
      allowed: "Zugänge",
      requests_inbox: "Anfragen",
      chat_inbox: "Nachrichten",
      updates_admin: "Wochenberichte",
      logout: "Abmelden",
      language: "Sprache",
    },
    dashboard: {
      greeting: "Willkommen",
      subline: "Ihr Zuhause ist in besten Händen.",
      status: {
        ready: "Bereit",
        attention: "Beachtung",
        in_progress: "In Bearbeitung",
        arrival_prep: "Ankunft vorbereitet",
        closed: "Geschlossen",
        emergency: "Notfall",
      },
      house_status: "Hausstatus",
      temperature: "Temperatur",
      heating: "Heizung",
      water: "Wasser",
      alarm: "Alarm",
      internet: "Internet",
      last_inspection: "Letzte Inspektion",
      next_inspection: "Nächste Inspektion",
      next_arrival: "Nächste Ankunft",
      request_anything: "Sonderwünsche",
      request_lead: "Ein Wunsch – wir kümmern uns um alles.",
      new_booking: "Abwesenheit planen",
      recent_activity: "Aktivität",
    },
    booking: {
      title: "Abwesenheit planen",
      lead: "Wählen Sie Ihren Zeitraum und optional gewünschte Extras. Standardleistungen sind bereits enthalten.",
      from: "Von",
      to: "Bis",
      property: "Objekt",
      services: "Leistungen",
      extras_label: "Extras (optional)",
      extras_hint: "Zusätzlich zum enthaltenen Leistungsumfang.",
      view_standard: "Leistungsumfang einsehen",
      recent_title: "Zuletzt geplant",
      recent_lead: "Ein Klick fügt einen Extra aus einer früheren Abwesenheit wieder hinzu.",
      notes: "Notizen für das Team",
      submit: "Abwesenheit einreichen",
      created: "Abwesenheit geplant.",
      upcoming: "Kommende Abwesenheiten",
      no_upcoming: "Noch keine Abwesenheiten geplant.",
    },
    chat: {
      title: "Nachrichten",
      lead: "Direkter Draht zu Ihrem Concierge-Team.",
      placeholder: "Nachricht schreiben…",
      send: "Senden",
      attach: "Bild anhängen",
      empty: "Noch keine Nachrichten. Schreiben Sie uns.",
      team: "Weissblom-Team",
      you: "Sie",
    },
    catalogue: {
      title: "Leistungen",
      lead: "Kuratierte Services für Ihr Objekt.",
      standard_button: "Leistungsumfang einsehen",
      standard_title: "Leistungsumfang",
      standard_lead: "Diese Leistungen sind in Ihrem Weissblom-Servicepaket enthalten.",
      extras_title: "Zusatzleistungen (Extras)",
      extras_lead: "Zusätzlich zur Standardbetreuung individuell buchbar.",
      packages_title: "Servicepakete",
      packages_lead: "Wählen Sie das passende Betreuungsintervall.",
      categories: {
        home_care: "Hausbetreuung",
        cleaning: "Reinigung",
        garden: "Garten",
        winter: "Winterdienst",
        maintenance: "Instandhaltung",
        lifestyle: "Lifestyle",
        interior: "Innenbereich",
        outdoor: "Außenbereich / Garten",
        additional: "Weitere Serviceleistungen",
      },
      request: "Anfragen",
    },
    requests: {
      title: "Anfragen",
      lead: "Ihre offenen und abgeschlossenen Anliegen.",
      new: "Neue Anfrage",
      form_title: "Beschreiben Sie Ihr Anliegen",
      title_field: "Kurzer Titel",
      description: "Beschreibung",
      priority: "Priorität",
      priority_options: { low: "Niedrig", normal: "Normal", high: "Hoch", urgent: "Dringend" },
      submit: "Anfrage senden",
      status: {
        new: "Neu",
        received: "Eingegangen",
        under_review: "In Prüfung",
        quote_required: "Angebot erforderlich",
        awaiting_approval: "Freigabe ausstehend",
        scheduled: "Geplant",
        requested: "Angefragt",
        confirmed: "Bestätigt",
        accepted: "Bestätigt",
        in_progress: "In Betreuung",
        in_service: "In Betreuung",
        completed: "Erledigt",
        done: "Erledigt",
        closed: "Erledigt",
        cancelled: "Storniert",
      },
      empty: "Noch keine Anfragen.",
    },
    team: {
      dashboard_title: "Team-Übersicht",
      dashboard_lead: "Überblick über alle Kunden, Abwesenheiten und offene Vorgänge.",
      stats: {
        customers: "Kunden",
        properties: "Objekte",
        upcoming_absences: "Bevorstehende Abwesenheiten",
        open_requests: "Offene Anfragen",
        unread_messages: "Ungelesene Nachrichten",
      },
      calendar_title: "Kalender",
      calendar_lead: "Alle Aufgaben und Termine des Teams in einem Kalender.",
      add_event: "Termin hinzufügen",
      customers_title: "Kundenverwaltung",
      allowed_title: "Zugelassene E-Mails",
      allowed_lead: "Nur zugelassene E-Mails können sich anmelden.",
      add_email: "E-Mail hinzufügen",
      email: "E-Mail",
      role: "Rolle",
      name: "Name",
      actions: "Aktionen",
      remove: "Entfernen",
      requests_title: "Sonderwünsche",
      chat_inbox_title: "Nachrichten-Eingang",
      no_customers: "Noch keine Kunden.",
      no_events: "Keine Termine.",
      months: ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"],
      weekdays: ["Mo","Di","Mi","Do","Fr","Sa","So"],
    },
    common: {
      loading: "Wird geladen…",
      cancel: "Abbrechen",
      save: "Speichern",
      confirm: "Bestätigen",
      close: "Schließen",
      error: "Fehler",
      today: "Heute",
      week: "Woche",
      month: "Monat",
    },
    updates: {
      title: "Wöchentliche Statusberichte",
      lead: "Einmal pro Woche fasst Ihr Team den Objektstatus mit Foto und Kurzbericht zusammen.",
      week_of: "Woche vom",
      photo: "Foto",
      photos: "Fotos",
      empty: "Noch kein Bericht vorhanden.",
      admin_title: "Wochenbericht senden",
      admin_lead: "Erstellen Sie den Wochenbericht für ein Objekt — der Kunde erhält Fotos und Zusammenfassung.",
      customer_label: "Kunde",
      property_label: "Objekt",
      week_label: "Woche (Montag)",
      summary_label: "Zusammenfassung",
      upload: "Fotos hochladen",
      add_more: "Weitere Fotos hinzufügen",
      remove_photo: "Entfernen",
      submit: "Bericht senden",
      sent: "Bericht gesendet.",
      delete: "Löschen",
      reminder_title: "Diese Woche noch offen",
      reminder_lead: "Wochenberichte für die aktuelle Kalenderwoche noch nicht versendet.",
      reminder_send_now: "Jetzt senden",
      reminder_all_done: "Alle Wochenberichte für diese Woche sind erledigt.",
      photo_of: "von",
    },
  },
  en: {
    brand: "Weissblom",
    subtitle: "Private Property Concierge",
    login: {
      title: "Welcome",
      lead: "Your private property concierge. Access is by invitation only.",
      emailLabel: "Email",
      emailPlaceholder: "your.address@example.com",
      requestCode: "Request access code",
      codeSent: "An 8-digit code has been sent to your email.",
      codeLabel: "8-digit access code",
      verify: "Sign in",
      back: "Different email",
      notAllowed: "This email is not on the invited list.",
      needAccess: "I don't have access yet",
      inviteHint: "New members are added by our team. Contact: info@weissblom.de",
    },
    nav: {
      dashboard: "Overview",
      booking: "Absence",
      chat: "Messages",
      requests: "Requests",
      catalogue: "Services",
      updates: "Weekly reports",
      handbook: "Handbook",
      family: "Family",
      settings: "Preferences",
      team_dashboard: "Team Overview",
      calendar: "Calendar",
      customers: "Customers",
      allowed: "Access",
      requests_inbox: "Requests",
      chat_inbox: "Messages",
      updates_admin: "Weekly reports",
      logout: "Sign out",
      language: "Language",
    },
    dashboard: {
      greeting: "Welcome",
      subline: "Your home is in the best hands.",
      status: { ready: "Ready", attention: "Attention", in_progress: "In Progress", arrival_prep: "Arrival Prep", closed: "Closed", emergency: "Emergency" },
      house_status: "House status",
      temperature: "Temperature",
      heating: "Heating",
      water: "Water",
      alarm: "Alarm",
      internet: "Internet",
      last_inspection: "Last inspection",
      next_inspection: "Next inspection",
      next_arrival: "Next arrival",
      request_anything: "Request anything",
      request_lead: "One request – we take care of everything.",
      new_booking: "Plan absence",
      recent_activity: "Activity",
    },
    booking: {
      title: "Plan absence",
      lead: "Pick your dates and optionally add extras. Standard services are already included.",
      from: "From",
      to: "To",
      property: "Property",
      services: "Services",
      extras_label: "Extras (optional)",
      extras_hint: "In addition to the included standard services.",
      view_standard: "View included services",
      recent_title: "Recently planned",
      recent_lead: "One tap re-adds an extra from an earlier absence.",
      notes: "Notes for the team",
      submit: "Submit absence",
      created: "Absence planned.",
      upcoming: "Upcoming absences",
      no_upcoming: "No upcoming absences.",
    },
    chat: {
      title: "Messages",
      lead: "Direct line to your concierge team.",
      placeholder: "Write a message…",
      send: "Send",
      attach: "Attach image",
      empty: "No messages yet. Say hello.",
      team: "Weissblom Team",
      you: "You",
    },
    catalogue: {
      title: "Services",
      lead: "Curated services for your property.",
      standard_button: "View included services",
      standard_title: "Included services",
      standard_lead: "The services below are part of every Weissblom service package.",
      extras_title: "Additional services (extras)",
      extras_lead: "Individually bookable on top of the standard care.",
      packages_title: "Service packages",
      packages_lead: "Choose the care interval that fits.",
      categories: { home_care: "Home Care", cleaning: "Cleaning", garden: "Garden", winter: "Winter", maintenance: "Maintenance", lifestyle: "Lifestyle", interior: "Interior", outdoor: "Outdoor / Garden", additional: "Additional services" },
      request: "Request",
    },
    requests: {
      title: "Requests",
      lead: "Your open and completed matters.",
      new: "New request",
      form_title: "Describe your request",
      title_field: "Short title",
      description: "Description",
      priority: "Priority",
      priority_options: { low: "Low", normal: "Normal", high: "High", urgent: "Urgent" },
      submit: "Send request",
      status: {
        new: "New", received: "Received", under_review: "Under Review", quote_required: "Quote Required",
        awaiting_approval: "Awaiting Approval", scheduled: "Scheduled",
        requested: "Requested", confirmed: "Confirmed", accepted: "Confirmed",
        in_progress: "In Service", in_service: "In Service",
        completed: "Done", done: "Done", closed: "Done",
        cancelled: "Cancelled",
      },
      empty: "No requests yet.",
    },
    team: {
      dashboard_title: "Team Overview",
      dashboard_lead: "All customers, absences and open matters at a glance.",
      stats: { customers: "Kunden", properties: "Objekte", upcoming_absences: "Bevorstehende Abwesenheiten", open_requests: "Offene Anfragen", unread_messages: "Ungelesene Nachrichten" },
      calendar_title: "Calendar",
      calendar_lead: "All team tasks and appointments in one calendar.",
      add_event: "Add event",
      customers_title: "Customer register",
      allowed_title: "Allowed emails",
      allowed_lead: "Only allowed emails can sign in.",
      add_email: "Add email",
      email: "Email",
      role: "Rolle",
      name: "Name",
      actions: "Actions",
      remove: "Remove",
      requests_title: "Request Anything",
      chat_inbox_title: "Message inbox",
      no_customers: "No customers yet.",
      no_events: "No events.",
      months: ["January","February","March","April","May","June","July","August","September","October","November","December"],
      weekdays: ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"],
    },
    common: { loading: "Loading…", cancel: "Cancel", save: "Save", confirm: "Confirm", close: "Close", error: "Error", today: "Today", week: "Week", month: "Month" },
    updates: {
      title: "Weekly status reports",
      lead: "Once a week your team summarises the property status with photos and a short report.",
      week_of: "Week of",
      photo: "Photo",
      photos: "Photos",
      empty: "No report yet.",
      admin_title: "Send weekly report",
      admin_lead: "Create the weekly report for a property — the customer receives photos and a summary.",
      customer_label: "Customer",
      property_label: "Property",
      week_label: "Week (Monday)",
      summary_label: "Summary",
      upload: "Upload photos",
      add_more: "Add more photos",
      remove_photo: "Remove",
      submit: "Send report",
      sent: "Report sent.",
      delete: "Delete",
      reminder_title: "Diese Woche noch offen",
      reminder_lead: "Wochenberichte für die aktuelle Kalenderwoche noch nicht versendet.",
      reminder_send_now: "Jetzt senden",
      reminder_all_done: "All weekly reports for this week are done.",
      photo_of: "of",
    },
  },
};

const I18nContext = createContext(null);

// Sprache: vor Login session-lokal (Default DE), nach Login serverseitig
// pro Nutzer in `users.ui_prefs.lang` — kein localStorage mehr.
export function I18nProvider({ children }) {
  const auth = useAuth();
  const user = auth?.user;
  const [localLang, setLocalLang] = useState("de");
  const serverLang = user?.ui_prefs?.lang;
  const lang = serverLang === "de" || serverLang === "en" ? serverLang : localLang;
  const setLang = (next) => {
    if (next !== "de" && next !== "en") return;
    setLocalLang(next);
    if (user?.id) auth.setUiPrefs({ lang: next });
  };
  const t = translations[lang] || translations.de;
  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>
  );
}

export function useI18n() { return useContext(I18nContext); }
